import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCoins, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import AuthContext from '../../context/AuthContext';
import { useAmazonSync } from '../../context/AmazonSyncContext';
import cardStyles from '../../components/UI/SelectProductCatalogueModal.module.css';
import AsinNotFoundModal from '../../components/ListingScore/components/AsinNotFoundModal';
import LoadingOverlay from '../../components/UI/LoadingOverlay';
import styles from './UpdateTitlePage.module.css';
import { CREDIT_COST_PER_TITLE } from './constants';
import AsinSubmitButton from './AsinSubmitButton';
import useSingleFlight from '../../hooks/useSingleFlight';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const GRID_COLUMNS = 3;
const GRID_ROWS = 3;
const PRODUCTS_PER_PAGE = GRID_COLUMNS * GRID_ROWS; // exactly 3 rows of 3 = 9 per page

// Standalone page for bulk-updating product titles: fetch by ASIN or pick from the
// Amazon catalogue, then update the selected listings' titles.
const UpdateTitlePage = () => {
    const authCtx = useContext(AuthContext);
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedAsins, setSelectedAsins] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [asinInput, setAsinInput] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [showAsinNotFoundModal, setShowAsinNotFoundModal] = useState(false);
    const [showAsin, setShowAsin] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [updateError, setUpdateError] = useState('');
    // Shown when a multi-ASIN run is queued as a background job.
    const [showQueuedPopup, setShowQueuedPopup] = useState(false);

    const { isBridgeActive } = useAmazonSync();
    const isAmazonConnected = authCtx.user?.amazon_status === true;

    const fetchCatalogue = useCallback(async () => {
        if (!isAmazonConnected || !authCtx.token) {
            setLoading(false);
            setProducts([]);
            return;
        }
        try {
            setLoading(true);
            const res = await fetch(`${API_URL}/oauth_amazon/amazon/catalogue`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${authCtx.token}`,
                    'Content-Type': 'application/json',
                },
            });
            if (!res.ok) throw new Error(`Failed to fetch products: ${res.status}`);
            const data = await res.json();
            setProducts(data.products || []);
        } catch (err) {
            console.error('Failed to load catalogue:', err);
            setProducts([]);
        } finally {
            setLoading(false);
        }
    }, [isAmazonConnected, authCtx.token]);

    useEffect(() => { fetchCatalogue(); }, [fetchCatalogue]);

    const filteredProducts = useMemo(() => {
        const query = searchTerm.trim().toLowerCase();
        if (!query) return products;
        return products.filter(p =>
            [p.title, p.sku, p.asin].some(field => field && field.toLowerCase().includes(query))
        );
    }, [products, searchTerm]);

    // Reset to the first page whenever the filtered result set changes.
    useEffect(() => { setCurrentPage(1); }, [searchTerm, products.length]);

    const totalPages = Math.max(1, Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE));
    const pageStart = (currentPage - 1) * PRODUCTS_PER_PAGE;
    const pageProducts = filteredProducts.slice(pageStart, pageStart + PRODUCTS_PER_PAGE);

    const selectedCount = selectedAsins.length;
    const allSelected = products.length > 0 && products.every(p => selectedAsins.includes(p.asin));

    const toggleAsin = (asin) => {
        setSelectedAsins(prev =>
            prev.includes(asin) ? prev.filter(a => a !== asin) : [...prev, asin]
        );
    };

    const toggleSelectAll = () => {
        // Dedupe: the catalogue can repeat an ASIN across SKUs.
        setSelectedAsins(allSelected ? [] : [...new Set(products.map(p => p.asin))]);
    };

    // Turn one or more typed/pasted ASINs into chips. Each must be exactly 10 chars
    // (same rule as the Listing Score input) and pass the open/close account gating:
    // external ASINs are allowed only when the account is open, Amazon isn't connected,
    // or the catalogue is still syncing (bridge active); otherwise → ASIN-not-found.
    const addTypedAsins = (values) => {
        let accountType = authCtx.user?.account_type;
        if (!accountType) {
            try { accountType = JSON.parse(localStorage.getItem('authUser'))?.account_type || 'close'; }
            catch { accountType = 'close'; }
        }
        const isOpenAccount = accountType === 'open';
        const allowExternal = !isAmazonConnected || isBridgeActive || isOpenAccount;

        const toAdd = [];
        let anyBlocked = false;
        values.forEach((raw) => {
            const asin = raw.trim().toUpperCase();
            if (asin.length !== 10) return;
            const inCatalogue = products.some(p => (p.asin || '').toUpperCase() === asin);
            if (!inCatalogue && !allowExternal) { anyBlocked = true; return; }
            // Case-insensitive uniqueness — never add an ASIN already selected/queued.
            const isDup = selectedAsins.some(a => (a || '').toUpperCase() === asin)
                || toAdd.includes(asin);
            if (!isDup) toAdd.push(asin);
        });
        if (toAdd.length) setSelectedAsins(prev => [...new Set([...prev, ...toAdd])]);
        if (anyBlocked) setShowAsinNotFoundModal(true);
    };

    const commitAsinInput = () => {
        if (!asinInput.trim()) return;
        addTypedAsins(asinInput.split(/[\s,]+/));
        setAsinInput('');
    };

    const handleAsinFetch = (e) => {
        e.preventDefault();
        commitAsinInput();
    };

    const handleAsinKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
            e.preventDefault();
            commitAsinInput();
        } else if (e.key === 'Backspace' && !asinInput && selectedAsins.length > 0) {
            toggleAsin(selectedAsins[selectedAsins.length - 1]);
        }
    };

    // Pasting multiple comma/space/newline-separated ASINs adds them as chips at once.
    const handleAsinPaste = (e) => {
        const text = e.clipboardData.getData('text');
        if (/[\s,]/.test(text)) {
            e.preventDefault();
            addTypedAsins(text.split(/[\s,]+/));
            setAsinInput('');
        }
    };

    // Generate a fresh title + item highlight for every selected ASIN, then show
    // them on the results page.
    const generateUpdatedTitles = async () => {
        if (selectedCount === 0 || updating) return;
        setUpdating(true);
        setUpdateError('');

        const known = new Map(products.map(p => [p.asin, p]));
        // Guarantee only unique ASINs are generated (case-insensitive), keeping the
        // first occurrence — belt-and-suspenders on top of input-time dedup.
        const uniqueAsins = [...new Map(selectedAsins.map(a => [(a || '').toUpperCase(), a])).values()];
        // image_url is sent for storage/results display; the backend strips it from
        // the Claude prompt, so it costs no tokens.
        const payload = uniqueAsins.map(asin => {
            const p = known.get(asin);
            return { asin, sku: p?.sku || '', title: p?.title || '', image_url: p?.image_url || '' };
        });

        const authHeaders = {
            'Content-Type': 'application/json',
            ...(authCtx.token ? { Authorization: `Bearer ${authCtx.token}` } : {}),
        };

        try {
            // Multiple ASINs → background queue (SQS/Celery). Just submit and show
            // the "task queued" popup; the user views results later in Projects /
            // Task History (no real-time results page for bulk).
            if (payload.length > 1) {
                const submitRes = await fetch(`${API_URL}/title-update/submit`, {
                    method: 'POST',
                    headers: authHeaders,
                    body: JSON.stringify({ asins: payload.map(p => p.asin) }),
                });
                const submitData = await submitRes.json();
                if (!submitRes.ok || !submitData.success) {
                    throw new Error(submitData.error || `Request failed: ${submitRes.status}`);
                }
                setShowQueuedPopup(true);
                return;
            }

            // Single ASIN → real-time sync generation.
            const res = await fetch(`${API_URL}/single-gen/generate-title-highlights`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({ products: payload }),
            });
            const data = await res.json();
            if (!res.ok || !data.success) {
                throw new Error(data.error || `Request failed: ${res.status}`);
            }

            // Typed ASINs aren't in the catalogue, so the payload has no title or
            // image for them — the backend resolves both from Rainforest and returns
            // them alongside the generated copy. Fall back to those, mirroring the
            // rows the backend persists for the Manage page.
            const byAsin = new Map((data.results || []).map(r => [r.asin, r]));
            const results = payload.map(p => {
                const generated = byAsin.get(p.asin) || {};
                const oldTitle = p.title || generated.old_title || '';
                return {
                    asin: p.asin,
                    sku: p.sku,
                    image_url: p.image_url || generated.image_url || '',
                    productTitle: oldTitle || p.asin,
                    oldTitle,
                    newTitle: generated.title || '',
                    highlight: generated.item_highlights || '',
                };
            });
            navigate('/dashboard/update-title-results', { state: { results } });
        } catch (err) {
            console.error('Title generation failed:', err);
            setUpdateError(err.message || 'Failed to generate titles. Please try again.');
        } finally {
            setUpdating(false);
        }
    };

    // Blocks rage clicks from queueing the same ASINs twice.
    const handleUpdateTitle = useSingleFlight(generateUpdatedTitles);

    // The queued job is already submitted, so clear the form on dismiss — leaving the
    // previous selection in place invites an accidental duplicate run.
    const dismissQueuedPopup = () => {
        setShowQueuedPopup(false);
        setSelectedAsins([]);
        setAsinInput('');
        setSearchTerm('');
        setCurrentPage(1);
        setUpdateError('');
    };

    const totalCost = (selectedCount || 1) * CREDIT_COST_PER_TITLE;

    // Key by absolute position, not ASIN alone: catalogues can list the same ASIN under
    // multiple SKUs, and duplicate React keys corrupt reconciliation across page changes
    // (stale extra cards left in the DOM).
    const renderCard = (product, idx) => {
        const isSelected = selectedAsins.includes(product.asin);
        return (
            <div key={`${product.asin}-${pageStart + idx}`} className={cardStyles.productCardWrapper}>
                <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleAsin(product.asin)}
                    className={cardStyles.productCheckbox}
                />
                <div
                    className={`${cardStyles.productCard} ${isSelected ? cardStyles.productCardSelected : ''}`}
                    onClick={() => toggleAsin(product.asin)}
                >
                    <div className={cardStyles.productCardInner}>
                        <div className={cardStyles.productImage}>
                            {product.image_url && (
                                <img
                                    src={product.image_url}
                                    alt={product.title || product.asin}
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                />
                            )}
                        </div>
                        <div className={cardStyles.productInfo}>
                            <div className={cardStyles.productInfoInner}>
                                <div className={cardStyles.productName}>{product.title || product.asin}</div>
                                <div className={cardStyles.skuTag}>
                                    <span>{showAsin ? `ASIN : ${product.asin || 'N/A'}` : `SKU ID : ${product.sku || 'N/A'}`}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={styles.page}>
            <h1 className={styles.title}>Update your Product Title</h1>
            <p className={styles.updateNote}>
                Takes 10–15 minutes depending on your ASIN count. Results will be
                sent to your registered email once ready.
            </p>
            <div className={styles.divider} />

            <div className={styles.body}>
                {/* ASIN lookup — typed / comma-separated 10-char ASINs become chips.
                    Catalogue picks land here as chips too, so the box always mirrors
                    the full selection; removing a chip unchecks its card. */}
                <form className={styles.asinLookupContainer} onSubmit={handleAsinFetch}>
                    <div className={`${styles.asinLookupCard} ${styles.chipsCard}`}>
                        <div className={styles.chipsWrapper}>
                            {selectedAsins.map((asin) => (
                                <span key={asin} className={styles.chip}>
                                    {asin}
                                    <button
                                        type="button"
                                        className={styles.chipRemove}
                                        onClick={() => toggleAsin(asin)}
                                        aria-label={`Remove ${asin}`}
                                    >
                                        <svg width="8" height="8" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M16 1.4L14.6 0L8 6.6L1.4 0L0 1.4L6.6 8L0 14.6L1.4 16L8 9.4L14.6 16L16 14.6L9.4 8L16 1.4Z" fill="currentColor"/>
                                        </svg>
                                    </button>
                                </span>
                            ))}
                            <input
                                type="text"
                                value={asinInput}
                                onChange={(e) => setAsinInput(e.target.value.toUpperCase())}
                                onKeyDown={handleAsinKeyDown}
                                onPaste={handleAsinPaste}
                                onBlur={commitAsinInput}
                                maxLength={10}
                                placeholder={selectedAsins.length === 0 ? 'Enter ASIN to fetch Product Listing' : ''}
                                className={styles.chipInput}
                            />
                        </div>
                        <AsinSubmitButton label="Fetch product listing" />
                    </div>
                </form>

                <div className={styles.orRow}>
                    <span className={styles.orLine} />
                    <span className={styles.orText}>Or</span>
                    <span className={styles.orLine} />
                </div>

                {/* Catalogue selection */}
                <div className={styles.catalogueSection}>
                    <h2 className={styles.catalogueHeading}>Select from Product Catalogue</h2>

                    <div className={styles.catalogueControls}>
                        <div className={styles.searchBar}>
                            <svg className={styles.searchIcon} width="16" height="16" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M10.421 0C14.5954 0 18 3.40462 18 7.57899C18 11.7534 14.5954 15.158 10.421 15.158C8.67422 15.158 7.06155 14.5613 5.77761 13.5621L1.61719 17.7224C1.24712 18.0925 0.647591 18.0925 0.277544 17.7224C-0.0925028 17.3524 -0.0925265 16.7519 0.277544 16.3828L4.43796 12.2234C3.43876 10.9383 2.84203 9.32678 2.84203 7.58006C2.84203 3.40569 6.24668 0.00106589 10.4211 0.00106589L10.421 0ZM10.421 1.89475C7.27068 1.89475 4.73667 4.42874 4.73667 7.57899C4.73667 10.7292 7.27068 13.2632 10.421 13.2632C13.5712 13.2632 16.1052 10.7292 16.1052 7.57899C16.1052 4.42874 13.5712 1.89475 10.421 1.89475Z" fill="#D2D2D2"/>
                            </svg>
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search for Product Name or SKU ID or ASIN"
                                className={styles.searchInput}
                            />
                        </div>

                        <div className={styles.summary}>
                            <div className={styles.skuToggle}>
                                <button
                                    type="button"
                                    className={`${styles.skuToggleBtn} ${!showAsin ? styles.skuToggleBtnActive : ''}`}
                                    onClick={() => setShowAsin(false)}
                                >
                                    SKU ID
                                </button>
                                <button
                                    type="button"
                                    className={`${styles.skuToggleBtn} ${showAsin ? styles.skuToggleBtnActive : ''}`}
                                    onClick={() => setShowAsin(true)}
                                >
                                    ASIN
                                </button>
                            </div>
                            <span className={styles.selectedCount}>{selectedCount}/{products.length} Selected</span>
                            <label className={styles.selectAll}>
                                <input
                                    type="checkbox"
                                    checked={allSelected}
                                    onChange={toggleSelectAll}
                                    className={cardStyles.productCheckbox}
                                />
                                <span>Select All</span>
                            </label>

                            {!loading && filteredProducts.length > 0 && totalPages > 1 && (
                                <div className={cardStyles.pagination}>
                                    <button
                                        type="button"
                                        className={cardStyles.pageButton}
                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                    >
                                        <FaChevronLeft />
                                    </button>
                                    <div className={cardStyles.pageIndicator}>
                                        <span>Page</span>
                                        <input
                                            type="number"
                                            min={1}
                                            max={totalPages}
                                            value={currentPage}
                                            onChange={(e) => {
                                                const value = Number(e.target.value);
                                                if (!value) return;
                                                setCurrentPage(Math.min(totalPages, Math.max(1, value)));
                                            }}
                                            onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                                            className={cardStyles.pageInput}
                                            aria-label="Page number"
                                        />
                                        <span>of {totalPages}</span>
                                    </div>
                                    <button
                                        type="button"
                                        className={cardStyles.pageButton}
                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                        disabled={currentPage === totalPages}
                                    >
                                        <FaChevronRight />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className={styles.grid}>
                        {loading ? (
                            <div className={styles.stateMsg}>Fetching your catalogue — give us a moment…</div>
                        ) : !isAmazonConnected ? (
                            <div className={styles.stateMsg}>Connect your Amazon account to load your catalogue.</div>
                        ) : filteredProducts.length === 0 ? (
                            <div className={styles.stateMsg}>
                                {products.length === 0 ? 'No products found.' : `No products match "${searchTerm}"`}
                            </div>
                        ) : (
                            pageProducts.map(renderCard)
                        )}
                    </div>
                </div>

                <button
                    type="button"
                    className={styles.updateBtn}
                    onClick={handleUpdateTitle}
                    disabled={selectedCount === 0 || updating}
                >
                    <span className={styles.creditChip}>
                        <FaCoins className={styles.creditIcon} />
                        {totalCost}
                    </span>
                    <span>{updating ? 'Generating…' : 'Update Title'}</span>
                </button>

                {updateError && <div className={styles.updateError}>{updateError}</div>}
            </div>

            {updating && <LoadingOverlay />}

            <AsinNotFoundModal
                isOpen={showAsinNotFoundModal}
                onClose={() => setShowAsinNotFoundModal(false)}
            />

            {/* Multi-ASIN runs are queued in the background (results also emailed),
                so the user can leave this page while it processes. */}
            {showQueuedPopup && (
                <div
                    style={{
                        position: 'fixed', inset: 0, zIndex: 1000, display: 'flex',
                        alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.45)',
                    }}
                    onClick={dismissQueuedPopup}
                    role="presentation"
                >
                    <div
                        style={{
                            background: '#fff', borderRadius: 20, padding: '36px 40px', maxWidth: 420,
                            textAlign: 'center', fontFamily: "'Plus Jakarta Sans', sans-serif",
                            boxShadow: '4px 26px 84px rgba(96,146,200,0.25)',
                        }}
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                    >
                        <h3 style={{ margin: 0, fontSize: 22, color: '#3A3A3A', fontFamily: "'Urbanist', sans-serif" }}>
                            Your task is in the queue
                        </h3>
                        <p style={{ margin: '14px 0 22px', fontSize: 14, color: '#737373', fontWeight: 500 }}>
                            We&apos;ll email you the results once they&apos;re ready. You can leave
                            this page — generation continues in the background. Once it&apos;s done,
                            you can also view the results anytime on the Projects page.
                        </p>
                        <button
                            type="button"
                            onClick={dismissQueuedPopup}
                            style={{
                                height: 44, padding: '0 24px', border: 'none', borderRadius: 14,
                                cursor: 'pointer', color: '#fff', fontSize: 15, fontWeight: 600,
                                background: 'linear-gradient(119deg,#2766AA 0%,#0752B0 100%)',
                            }}
                        >
                            Okay
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UpdateTitlePage;
